<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { useVideos } from '@ilteducation/videos';
import { useUserContext } from '@ilteducation/ilt-auth';

const props = withDefaults(
  defineProps<{
    // Resource ids in `${videoId}-${realm}` format.
    resourceIds: string[];
    size?: number;
  }>(),
  {
    size: 64,
  },
);

const { getVideoById } = useVideos();
const { realm } = useUserContext();

const MAX_THUMBNAILS = 4;

const thumbnails = ref<(string | undefined)[]>([]);
const isLoading = ref(true);

// Resource ids are the raw video id suffixed with `-${realm}`; getVideoById expects the raw id.
const toVideoId = (resourceId: string) => {
  const suffix = `-${realm}`;
  return resourceId.endsWith(suffix) ? resourceId.slice(0, -suffix.length) : resourceId;
};

const loadThumbnails = async () => {
  isLoading.value = true;
  const ids = props.resourceIds.slice(0, MAX_THUMBNAILS);
  const videos = await Promise.all(ids.map((id) => getVideoById(toVideoId(id)).catch(() => undefined)));
  thumbnails.value = videos.map((video) => video?.thumbnail);
  isLoading.value = false;
};

onMounted(loadThumbnails);
watch(() => props.resourceIds, loadThumbnails);
</script>

<template>
  <div
    class="thumbnail-icon"
    :class="`count-${thumbnails.length}`"
    :style="{ width: `${size}px`, height: `${size}px` }"
  >
    <div v-if="isLoading" class="thumbnail-cell thumbnail-full d-flex align-center justify-center">
      <v-progress-circular indeterminate size="16" width="2" color="white"></v-progress-circular>
    </div>

    <div v-else-if="thumbnails.length === 0" class="thumbnail-cell thumbnail-full thumbnail-fallback">
      <v-icon icon="fa:fawsb fa-video" size="small" color="white"></v-icon>
    </div>

    <div v-for="(thumbnail, index) in thumbnails" v-else :key="index" class="thumbnail-cell">
      <v-img v-if="thumbnail" :src="thumbnail" cover class="fill-height"></v-img>
      <div v-else class="thumbnail-fallback fill-height">
        <v-icon icon="fa:fawsb fa-video" size="x-small" color="white"></v-icon>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.thumbnail-icon {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 2px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  background-color: rgba(255, 255, 255, 0.1);
}

.thumbnail-cell {
  overflow: hidden;
  background-color: rgba(255, 255, 255, 0.05);
}

.thumbnail-full {
  grid-column: 1 / 3;
  grid-row: 1 / 3;
}

.thumbnail-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.5;
}

// 1 thumbnail: fills the whole square.
.count-1 .thumbnail-cell {
  grid-column: 1 / 3;
  grid-row: 1 / 3;
}

// 2 thumbnails: two full-height columns.
.count-2 .thumbnail-cell {
  grid-row: 1 / 3;
}

// 3 thumbnails: first one full-height on the left, the other two stacked on the right.
.count-3 .thumbnail-cell:first-child {
  grid-row: 1 / 3;
}

// 4 thumbnails: default 2x2 grid.
</style>
